<template>
  <v-col cols="12">
    <div class="mb-4 questionText d-flex align-start">
      <div
        class="mr-3"
        v-if="question.questionNumber && question.questionNumber.length > 0"
      >
        {{ question.questionNumber }}
      </div>
      <survey-question-text :text="question.text"></survey-question-text>
    </div>

    <table class="w-100 " :class="{ stripped: $vuetify.breakpoint.lgAndUp }">
      <thead>
        <th class="px-5 py-4"></th>
        <th
          class="vertical-align-bottom text-center text-capitalize  pt-5 pb-2 py-4  d-none d-lg-table-cell"
          v-for="(option, index) in question.options"
          :key="index"
        >
          <survey-question-text :text="option.text"></survey-question-text>
        </th>
      </thead>
      <tr
        v-for="child in question.children"
        :key="child.key"
        :class="{
          'red lighten-4': validated && invalid && !childAnswered(child.key)
        }"
      >
        <td class="px-5 py-4">
          <div class="mb-6 mb-lg-0 font-weight-bold d-flex align-center">
            <v-chip
              v-if="validated && invalid && !childAnswered(child.key)"
              color="red darken-2 white--text"
              class="text-capitalize mr-3 flex-shrink-0"
              >Required</v-chip
            >
            <template v-else>
              <v-chip
                v-if="child.questionNumber && child.questionNumber.length > 0"
                color="secondary"
                class="text-capitalize mr-3 flex-shrink-0"
              >
                {{ child.questionNumber }}
              </v-chip>
            </template>
            <survey-question-text
              class="d-inline"
              :text="child.text"
            ></survey-question-text>
          </div>
          <div class="d-block d-lg-none">
            <radio-list-btn
              v-for="(option, index) in question.options"
              :key="index"
              :text="option.text"
              :value="optionSelected(child.key, option.value)"
              @click="setKeyPair(child.key, option.value)"
              :inError="validated && invalid && !childAnswered(child.key)"
            ></radio-list-btn>
          </div>
        </td>
        <td
          class="text-center px-5 py-4 d-none d-lg-table-cell"
          v-for="(option, index) in question.options"
          :key="index"
          :width="columnWidth + 'px'"
        >
          <v-tooltip bottom open-delay="250">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                block
                large
                color="secondary"
                :text="!optionSelected(child.key, option.value)"
                class="text-h6"
                @click="setKeyPair(child.key, option.value)"
                v-bind="attrs"
                v-on="on"
              >
                <font-awesome-icon
                  v-if="optionSelected(child.key, option.value)"
                  icon="check-circle"
                ></font-awesome-icon>
                <font-awesome-icon
                  v-else
                  :class="
                    `${
                      validated && invalid && !childAnswered(child.key)
                        ? 'red--text'
                        : 'grey--text darken--4'
                    }`
                  "
                  :icon="['far', 'circle']"
                ></font-awesome-icon>
              </v-btn>
            </template>
            <span>{{ option.text }}</span>
          </v-tooltip>
        </td>
      </tr>
    </table>
  </v-col>
</template>

<style>
.questionText {
  font-size: 1.1rem;
}
</style>

<script>
import Vue from "vue";
import {
  getQuestionSetting,
  questionSettings,
  showQuestion,
  requireQuestion
} from "../constants/Survey";
import RadioListBtn from "./RadioList.Btn.vue";
import SurveyQuestionText from "./Survey.Question.Text.vue";
import { isEmptyOrSpaces } from "../utilities/dataUtilities";

export default {
  components: { RadioListBtn, SurveyQuestionText },
  data: () => ({
    innerValue: [],
    validated: false
  }),
  props: {
    value: {
      type: Array,
      default: () => []
    },
    question: {
      type: Object
    }
  },
  computed: {
    columnWidth() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.columnWidth.key
        ).value || requireQuestion(this.question, this.innerValue)
      );
    },
    required() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.required.key
        ).value == "true"
      );
    },
    fieldValues() {
      let results = [];

      for (let i = 0; i < this.question.children.length; i++) {
        results.push(
          this.innerValue.find(y => y.key === this.question.children[i].key)
        );
      }

      return results;
    },
    valid() {
      return (
        (this.required && this.completed) ||
        (this.started && this.completed) ||
        (!this.started && !this.required)
      );
    },
    invalid() {
      return !this.valid;
    },
    started() {
      let result = false;

      for (let i = 0; i < this.fieldValues.length; i++) {
        if (
          this.fieldValues[i] &&
          !isEmptyOrSpaces(this.fieldValues[i].value)
        ) {
          result = true;
          break;
        }
      }

      return result;
    },
    completed() {
      return this.errors.length === 0;
    },
    errors() {
      let errors = [];

      for (let i = 0; i < this.fieldValues.length; i++) {
        if (
          !this.fieldValues[i] ||
          isEmptyOrSpaces(this.fieldValues[i].value)
        ) {
          if (this.required || this.started) {
            errors.push({ key: this.fieldValues[i].key, value: "Required" });
          }
        }
      }

      return errors;
    },
    visible() {
      return showQuestion(this.question, this.innerValue);
    }
  },
  watch: {
    value(val) {
      if (!keyPairArraysAreEqual(val, this.innerValue)) {
        this.innerValue = val;
      }
    },
    innerValue(value, prev) {
      if (!keyPairArraysAreEqual(value, prev)) {
        this.$emit("input", this.innerValue);
      }
    }
  },
  methods: {
    setKeyPair(key, value) {
      let keyPair = { key, value };

      let index = this.innerValue.findIndex(x => x.key === key);
      if (index > -1) Vue.set(this.innerValue, index, keyPair);
      else this.innerValue.push(keyPair);
    },
    optionSelected(key, value) {
      let answer = this.innerValue.find(x => x.key === key);
      return answer ? answer.value === value : false;
    },
    childAnswered(key) {
      return !isEmptyOrSpaces(this.innerValue.find(x => x.key === key).value);
    },
    validate() {
      this.validated = true;
      return this.valid;
    },
    reset() {
      this.validated = false;
    },
    getQuestionSetting
  },
  mounted() {
    if (this.value) this.innerValue = this.value;
  },
  destroyed() {
    if (!showQuestion(this.question, this.innerValue)) {
      this.question.children.forEach(x => this.setKeyPair(x.key, null));
    }
  }
};

function keyPairArraysAreEqual(a, b) {
  return flatten(a) === flatten(b);

  function flatten(arr) {
    return JSON.parse(JSON.stringify(arr));
  }
}
</script>
